import { AnimatedLogo } from '@/components/AnimatedLogo';
import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { Loader2, Mail, Lock, User, Building2, Phone, Check, ArrowRight, ArrowLeft, Sparkles, CreditCard, Shield, ShieldCheck, Eye, Users, Laptop, Headset, Contact, GraduationCap, Wallet } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

// ── Reusable 6-box OTP input ─────────────────────────────────────────────────
// Auto-focus next on entry, backspace to previous, full paste support,
// auto-submit on the 6th digit, shake on error, green ring on success.
function OTPBoxes({ value, onChange, onComplete, status, disabled }: {
  value: string; onChange: (v: string) => void; onComplete: (v: string) => void;
  status: 'idle' | 'error' | 'success'; disabled?: boolean;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const chars = Array.from({ length: 6 }, (_, i) => value[i] || '');

  const writeAt = (i: number, d: string) => {
    const next = (value.slice(0, i) + d + value.slice(i + 1)).slice(0, 6);
    onChange(next);
    return next;
  };
  const handleChange = (i: number, raw: string) => {
    const d = raw.replace(/\D/g, '');
    if (!d) { writeAt(i, ''); return; }
    if (d.length > 1) { // a paste landed in one box
      const next = (value.slice(0, i) + d).replace(/\D/g, '').slice(0, 6);
      onChange(next);
      const last = Math.min(next.length, 5);
      refs.current[last]?.focus();
      if (next.length === 6) onComplete(next);
      return;
    }
    const next = writeAt(i, d);
    if (i < 5) refs.current[i + 1]?.focus();
    if (next.length === 6 && !next.includes('')) onComplete(next);
  };
  const handleKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[i] && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < 5) refs.current[i + 1]?.focus();
  };
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const d = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!d) return;
    onChange(d);
    refs.current[Math.min(d.length, 5)]?.focus();
    if (d.length === 6) onComplete(d);
  };

  const ring = status === 'error' ? '#dc2626' : status === 'success' ? '#22c55e' : '#2563eb';
  const idle = status === 'error' ? '#fca5a5' : status === 'success' ? '#86efac' : '#e2e8f0';

  return (
    <div onPaste={handlePaste}
      style={{ display: 'flex', gap: 8, justifyContent: 'space-between',
        animation: status === 'error' ? 'otpShake .4s' : undefined }}>
      {chars.map((c, i) => (
        <input key={i} ref={el => (refs.current[i] = el)}
          inputMode="numeric" autoComplete="one-time-code" maxLength={1} value={c}
          disabled={disabled || status === 'success'}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKey(i, e)}
          onFocus={e => e.target.select()}
          style={{
            width: '100%', aspectRatio: '1', minWidth: 0, textAlign: 'center',
            fontSize: 22, fontWeight: 700, color: '#0f172a',
            background: status === 'success' ? '#f0fdf4' : '#fff',
            border: `2px solid ${c ? ring : idle}`, borderRadius: 12,
            outline: 'none', transition: 'border-color .15s', fontFamily: 'inherit',
            cursor: disabled || status === 'success' ? 'default' : 'text',
          }} />
      ))}
    </div>
  );
}

const PLANS = [
  { id:'free', name:'Free', price:0, devices:5, ret:'7 days', color:'#22c55e', features:['5 Devices','Basic monitoring','7-day data retention','Email support'] },
  { id:'starter', name:'Starter', price:499, devices:15, ret:'30 days', color:'#3b82f6', features:['15 Devices','All monitoring features','AI scoring','Mobile app','30-day retention'] },
  { id:'business', name:'Business', price:999, devices:50, ret:'90 days', color:'#2563eb', pop:true, features:['50 Devices','Geofencing + GPS','Device management','Advanced reports','90-day retention'] },
  { id:'enterprise', name:'Enterprise', price:2499, devices:999, ret:'365 days', color:'#eab308', features:['Unlimited Devices','API + Webhooks','Dedicated manager','365-day retention','SLA guarantee'] },
];

const TOOLS = [
  { key:'monitor',  name:'Monitor 360',   Icon:Eye,           desc:'Employee monitoring & productivity', price:249, unit:'user',    color:'#2563eb' },
  { key:'hrms',     name:'HRMS 360 Pro',  Icon:Users,         desc:'Attendance, payroll & leave',         price:149, unit:'user',    color:'#16a34a' },
  { key:'assets',   name:'IT Assets Hub', Icon:Laptop,        desc:'IT asset & inventory tracking',       price:99,  unit:'user',    color:'#7c3aed' },
  { key:'helpdesk', name:'Helpdesk',      Icon:Headset,       desc:'IT service desk & tickets',           price:99,  unit:'user',    color:'#ea580c' },
  { key:'crm',      name:'Pulse CRM',   Icon:Contact,       desc:'Leads, deals & sales pipeline',       price:199, unit:'user',    color:'#0891b2' },
  { key:'edutrack', name:'EduTrack 360',  Icon:GraduationCap, desc:'School / College ERP',                price:49,  unit:'student', color:'#db2777' },
  { key:'finance',  name:'FinanceFlow AI', Icon:Wallet,       desc:'AI finance, accounting & insights',   price:199, unit:'user',    color:'#0d9488' },
];

const STEPS = ['Choose Tools','Company Info','Plan & License','Review'];

function F({ icon, label, ph, val, onChange, type='text' }: any) {
  return (
    <div>
      <label style={{ display:'block', fontSize:12, color:'#475569', marginBottom:6, fontWeight:600 }}>{label}</label>
      <div style={{ display:'flex', alignItems:'center', gap:10, background:'#fff', border:'1px solid #e2e8f0', borderRadius:10, padding:'0 14px', transition:'border-color .2s', minWidth:0 }}>
        {icon}
        <input type={type} placeholder={ph} value={val} onChange={e=>onChange(e.target.value)}
          style={{ flex:1, minWidth:0, width:'100%', boxSizing:'border-box', background:'transparent', border:'none', padding:'13px 0', color:'#0f172a', fontSize:14, outline:'none', fontFamily:'inherit' }} />
      </div>
    </div>
  );
}

export default function Register() {
  const [searchParams] = useSearchParams();
  const navigate = useSafeNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(searchParams.get('plan') || 'business');
  const [form, setForm] = useState({ companyName:'', fullName:'', email:'', password:'', confirmPassword:'', phone:'' });
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));
  const [products, setProducts] = useState<string[]>(['monitor']);
  const toggleProduct = (k: string) => setProducts(p => p.includes(k) ? p.filter(x => x !== k) : [...p, k]);

  // ── OTP email verification ──────────────────────────────────────────────
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpStatus, setOtpStatus] = useState<'idle' | 'error' | 'success'>('idle');
  const [otpMsg, setOtpMsg] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);
  const [verifiedToken, setVerifiedToken] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [otpLocked, setOtpLocked] = useState(false);
  const emailVerified = !!verifiedToken && verifiedEmail === form.email.trim().toLowerCase();

  // Only accept work/company email — block common personal providers.
  const FREE_EMAIL_DOMAINS = ['gmail.com','googlemail.com','yahoo.com','yahoo.co.in','yahoo.in','hotmail.com','outlook.com','live.com','msn.com','icloud.com','me.com','aol.com','proton.me','protonmail.com','rediffmail.com','ymail.com','gmx.com','mail.com'];
  const isWorkEmail = (email: string) => { const d = email.split('@')[1]?.toLowerCase().trim(); return !!d && !FREE_EMAIL_DOMAINS.includes(d); };

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const sendOtp = async () => {
    const email = form.email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Enter a valid email first'); return; }
    if (!isWorkEmail(email)) { setError("Please use your work email (e.g. you@yourcompany.com). Personal addresses like Gmail aren't accepted."); return; }
    setOtpBusy(true); setError(''); setOtpMsg(''); setOtpStatus('idle');
    try {
      const res = await fetch(API + '/api/auth/send-otp', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, type: 'registration' }),
      });
      const data = await res.json();
      if (!res.ok) { if (/lock/i.test(data.error || '')) { setOtpLocked(true); setResendIn(0); } throw new Error(data.error || 'Could not send code'); }
      setOtpSent(true); setOtp(''); setOtpStatus('idle');
      setResendIn(data.resendInSec || 60);
      setOtpMsg(`Code sent to ${email}. Check your inbox.`);
    } catch (e: any) { setError(e.message); }
    finally { setOtpBusy(false); }
  };

  const verifyOtp = async (code: string) => {
    const email = form.email.trim().toLowerCase();
    setOtpBusy(true); setOtpMsg('');
    try {
      const res = await fetch(API + '/api/auth/verify-otp', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp: code, type: 'registration' }),
      });
      const data = await res.json();
      if (!res.ok || !data.verified) throw new Error(data.error || 'Invalid code');
      setOtpStatus('success'); setVerifiedToken(data.token); setVerifiedEmail(email);
      setOtpMsg('Email verified ✓');
    } catch (e: any) {
      const locked = /lock/i.test(e.message || '');
      if (locked) { setOtpLocked(true); setResendIn(0); }
      setOtpStatus('error'); setOtpMsg(e.message);
      if (!locked) setTimeout(() => { setOtp(''); setOtpStatus('idle'); }, 600);
    } finally { setOtpBusy(false); }
  };

  // Changing the email after verifying invalidates the verification.
  const onEmailChange = (v: string) => {
    set('email', v);
    if (otpSent || verifiedToken || otpLocked) {
      setOtpSent(false); setOtp(''); setOtpStatus('idle'); setOtpMsg('');
      setVerifiedToken(''); setVerifiedEmail(''); setResendIn(0); setOtpLocked(false);
    }
  };
  const comboHint = () => {
    const has = (k: string) => products.includes(k);
    if (products.length === TOOLS.length) return '⭐ Full Suite — best value with every tool on one login.';
    if (has('assets') && has('helpdesk') && !has('monitor') && !has('hrms')) return 'ITSM Suite — save ₹49/user/month with IT Assets + Helpdesk together.';
    if (has('monitor') && has('hrms') && !has('assets') && !has('helpdesk')) return 'Monitor + HRMS bundle — save ₹49/user/month.';
    return '';
  };

  const validate = () => {
    if (!form.companyName.trim() || form.companyName.length<2) return 'Company name required (min 2 chars)';
    if (!form.fullName.trim()) return 'Full name required';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Valid email required';
    if (!isWorkEmail(form.email.trim().toLowerCase())) return 'Use your work email — personal addresses (Gmail, Yahoo…) are not accepted';
    if (form.phone.replace(/\D/g,'').length < 8) return 'Valid phone number required (include country code)';
    if (!form.password || form.password.length<6) return 'Password minimum 6 characters';
    if (form.password !== form.confirmPassword) return 'Passwords do not match';
    return '';
  };

  const nextStep = () => {
    if (step===1) { if(!products.length){ setError('Select at least one tool'); return; } setError(''); }
    if (step===2) {
      const e=validate(); if(e){setError(e);return;}
      if(!emailVerified){ setError('Please verify your email with the code we sent before continuing'); return; }
      setError('');
    }
    setStep(s => Math.min(s+1, 4));
  };
  const prevStep = () => { setError(''); setStep(s => Math.max(s-1, 1)); };

  const handleSubmit = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(API+'/api/register/signup', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ ...form, plan: selectedPlan, products, verifiedToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Registration failed');
      localStorage.setItem('dk_token', data.token);
      localStorage.setItem('dk_user', JSON.stringify(data.user));
      if (selectedPlan==='free') navigate('/');
      else window.location.href = '/pay?plan='+selectedPlan+'&orgId='+data.org.id+'&email='+encodeURIComponent(form.email.trim().toLowerCase());
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  const plan = PLANS.find(p=>p.id===selectedPlan) || PLANS[2];

  return (
    <div style={{ minHeight:'100vh', background:'linear-gradient(135deg,#f8fafc,#eff6ff)', display:'flex', alignItems:'center', justifyContent:'center', padding:'24px 14px', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <div style={{ maxWidth:520, width:'100%' }}>

        {/* Animated Logo */}
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', marginBottom:8 }}>
          <AnimatedLogo size={90} />
          <h1 style={{ fontSize:26, fontWeight:800, marginTop:4 }}>
            <span style={{color:'#2563EB'}}>Prodi</span><span style={{color:'#0F172A'}}>fy</span><span style={{color:'#22C55E',fontSize:16,verticalAlign:'super'}}>360</span>
          </h1>
          <p style={{ color:'#64748b', fontSize:13, marginTop:4 }}>Create your account in 4 simple steps</p>
        </div>

        {/* Step indicator */}
        <div style={{ display:'flex', gap:4, marginBottom:28, padding:'0 8px' }}>
          {STEPS.map((label,i) => {
            const s = i+1;
            const active = step >= s;
            const done = step > s;
            return (
              <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
                <div style={{ display:'flex', alignItems:'center', width:'100%' }}>
                  {i>0 && <div style={{ flex:1, height:2, background:active?'#2563eb':'#e2e8f0', borderRadius:2, transition:'background .3s' }}/>}
                  <div style={{ width:32, height:32, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, background:active?'#2563eb':'#e2e8f0', color:active?'#fff':'#94a3b8', transition:'all .3s', flexShrink:0 }}>
                    {done ? <Check size={14}/> : s}
                  </div>
                  {i<STEPS.length-1 && <div style={{ flex:1, height:2, background:step>s?'#2563eb':'#e2e8f0', borderRadius:2, transition:'background .3s' }}/>}
                </div>
                <span style={{ fontSize:11, fontWeight:600, color:active?'#0f172a':'#94a3b8', transition:'color .3s' }}>{label}</span>
              </div>
            );
          })}
        </div>

        {/* Card */}
        <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:20, padding:'28px clamp(16px,5vw,28px)', boxShadow:'0 4px 24px rgba(0,0,0,0.04)', boxSizing:'border-box' }}>

          {error && <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:12, padding:'12px 16px', marginBottom:20, color:'#dc2626', fontSize:13, fontWeight:500 }}>{error}</div>}

          {/* STEP 1: Choose Tools */}
          {step===1 && (
            <div>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, marginBottom:16, flexWrap:'wrap' }}>
                <div>
                  <h2 style={{ fontSize:18, fontWeight:700, color:'#0f172a', marginBottom:4 }}>What does your team need?</h2>
                  <p style={{ fontSize:13, color:'#94a3b8' }}>Pick one or more apps — add the rest anytime. One login, everything works together.</p>
                </div>
                <button type="button" onClick={()=>setProducts(products.length===TOOLS.length ? ['monitor'] : TOOLS.map(t=>t.key))}
                  style={{ flexShrink:0, fontSize:12, fontWeight:700, color:'#2563eb', background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:8, padding:'7px 12px', cursor:'pointer' }}>
                  {products.length===TOOLS.length ? 'Clear all' : 'Select all'}
                </button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(2,minmax(0,1fr))', gap:12 }}>
                {TOOLS.map(t => {
                  const on = products.includes(t.key);
                  const Ic = t.Icon;
                  return (
                    <div key={t.key} onClick={()=>toggleProduct(t.key)} role="checkbox" aria-checked={on}
                      style={{ position:'relative', background:on?'#f5f8ff':'#fff', border:'2px solid '+(on?t.color:'#e8edf4'), borderRadius:14, padding:'15px 14px', cursor:'pointer', transition:'all .15s', boxShadow:on?'0 6px 16px '+t.color+'22':'0 1px 2px rgba(15,23,42,.04)' }}>
                      <div style={{ position:'absolute', top:9, right:9, width:20, height:20, borderRadius:'50%', border:'2px solid '+(on?t.color:'#cbd5e1'), background:on?t.color:'#fff', display:'grid', placeItems:'center', transition:'all .15s' }}>
                        {on && <Check size={12} color="#fff" strokeWidth={3}/>}
                      </div>
                      <div style={{ width:44, height:44, borderRadius:12, background:'linear-gradient(135deg,'+t.color+','+t.color+'cc)', display:'grid', placeItems:'center', marginBottom:10, boxShadow:'0 4px 10px '+t.color+'33' }}><Ic size={22} color="#fff" strokeWidth={2}/></div>
                      <div style={{ fontSize:14, fontWeight:700, color:'#0f172a', lineHeight:1.2 }}>{t.name}</div>
                      <div style={{ fontSize:11.5, color:'#64748b', marginTop:3, minHeight:'2.6em', lineHeight:1.3 }}>{t.desc}</div>
                      <div style={{ fontSize:13, fontWeight:800, color:t.color, marginTop:7 }}>₹{t.price}<span style={{ fontSize:10, color:'#94a3b8', fontWeight:600 }}> /{t.unit}/mo</span></div>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop:12, fontSize:12.5, color:'#64748b', textAlign:'center', fontWeight:600 }}>{products.length} app{products.length===1?'':'s'} selected</div>
              {comboHint() && (
                <div style={{ marginTop:14, background:'#ecfdf5', border:'1px solid #a7f3d0', borderRadius:10, padding:'10px 14px', fontSize:12.5, fontWeight:600, color:'#047857' }}>💡 {comboHint()}</div>
              )}
              <button onClick={nextStep}
                style={{ width:'100%', marginTop:20, padding:14, background:'linear-gradient(135deg,#2563eb,#1d4ed8)', color:'#fff', border:'none', borderRadius:12, fontSize:15, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                Continue <ArrowRight size={16}/>
              </button>
            </div>
          )}

          {/* STEP 2: Company Info */}
          {step===2 && (
            <div>
              <h2 style={{ fontSize:18, fontWeight:700, color:'#0f172a', marginBottom:4 }}>Company Information</h2>
              <p style={{ fontSize:13, color:'#94a3b8', marginBottom:24 }}>Tell us about your organization</p>
              <div style={{ display:'grid', gap:16 }}>
                <F icon={<Building2 size={16} color="#94a3b8"/>} label="Company Name *" ph="Enter your company name" val={form.companyName} onChange={(v:string)=>set('companyName',v)} />
                <F icon={<User size={16} color="#94a3b8"/>} label="Full Name *" ph="Enter your full name" val={form.fullName} onChange={(v:string)=>set('fullName',v)} />
                <div>
                  <F icon={<Mail size={16} color="#94a3b8"/>} label="Work Email *" ph="you@company.com" val={form.email} onChange={onEmailChange} type="email" />

                  {/* Email verification */}
                  {!emailVerified && !otpSent && (
                    <button type="button" onClick={sendOtp} disabled={otpBusy}
                      style={{ marginTop:10, width:'100%', padding:'11px', background:'#eff6ff', color:'#2563eb', border:'1px solid #bfdbfe', borderRadius:10, fontSize:13, fontWeight:700, cursor:otpBusy?'default':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:7, opacity:otpBusy?0.7:1 }}>
                      {otpBusy ? <Loader2 size={14} style={{animation:'spin 1s linear infinite'}}/> : <Shield size={14}/>}
                      {otpBusy ? 'Sending code…' : 'Send OTP to verify email'}
                    </button>
                  )}

                  {emailVerified && (
                    <div style={{ marginTop:10, padding:'11px 14px', background:'#f0fdf4', border:'1px solid #86efac', borderRadius:10, fontSize:13, fontWeight:700, color:'#16a34a', display:'flex', alignItems:'center', gap:7 }}>
                      <ShieldCheck size={15}/> Email verified
                    </div>
                  )}

                  {otpSent && !emailVerified && (
                    <div style={{ marginTop:12, padding:16, background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:12 }}>
                      <div style={{ fontSize:12.5, color:'#475569', marginBottom:10, fontWeight:600 }}>Enter the 6-digit code sent to your email</div>
                      <OTPBoxes value={otp} onChange={setOtp} onComplete={verifyOtp} status={otpStatus} disabled={otpBusy} />
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12 }}>
                        <span style={{ fontSize:12, color: otpStatus==='error' ? '#dc2626' : '#64748b', fontWeight: otpStatus==='error'?600:400 }}>{otpMsg}</span>
                        {otpLocked
                          ? <span style={{ fontSize:12, color:'#dc2626', fontWeight:600 }}>Locked for 30 min</span>
                          : resendIn > 0
                          ? <span style={{ fontSize:12, color:'#94a3b8' }}>Resend in {resendIn}s</span>
                          : <button type="button" onClick={sendOtp} disabled={otpBusy} style={{ fontSize:12, color:'#2563eb', fontWeight:700, background:'none', border:'none', cursor:'pointer', padding:0 }}>Resend code</button>}
                      </div>
                      {otpLocked && <div style={{ marginTop:10, fontSize:12, color:'#b91c1c', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, padding:'8px 10px' }}>Too many attempts. For your security this email is locked for 30 minutes. Please try again later or use a different work email.</div>}
                      <button type="button" onClick={()=>verifyOtp(otp)} disabled={otpBusy || otp.length<6 || otpLocked}
                        style={{ width:'100%', marginTop:12, padding:'12px', background:(otp.length<6||otpBusy)?'#cbd5e1':'linear-gradient(135deg,#2563eb,#1d4ed8)', color:'#fff', border:'none', borderRadius:10, fontSize:14, fontWeight:700, cursor:(otp.length<6||otpBusy)?'default':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:7 }}>
                        {otpBusy ? <Loader2 size={14} style={{animation:'spin 1s linear infinite'}}/> : <Check size={15}/>} Verify & Continue
                      </button>
                    </div>
                  )}
                </div>
                <F icon={<Phone size={16} color="#94a3b8"/>} label="Phone Number *" ph="e.g. +91 98765 43210 (include country code)" val={form.phone} onChange={(v:string)=>set('phone',v)} type="tel" />
                <div style={{ display:'grid', gridTemplateColumns:'minmax(0,1fr) minmax(0,1fr)', gap:12 }}>
                  <F icon={<Lock size={16} color="#94a3b8"/>} label="Password *" ph="Min 6 characters" val={form.password} onChange={(v:string)=>set('password',v)} type="password" />
                  <F icon={<Lock size={16} color="#94a3b8"/>} label="Confirm Password *" ph="Re-enter password" val={form.confirmPassword} onChange={(v:string)=>set('confirmPassword',v)} type="password" />
                </div>
              </div>
              <div style={{ display:'flex', gap:12, marginTop:24 }}>
                <button onClick={prevStep} style={{ flex:1, padding:14, background:'#f1f5f9', color:'#475569', border:'1px solid #e2e8f0', borderRadius:12, fontSize:14, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                  <ArrowLeft size={16}/> Back
                </button>
                <button onClick={nextStep}
                  style={{ flex:2, padding:14, background:'linear-gradient(135deg,#2563eb,#1d4ed8)', color:'#fff', border:'none', borderRadius:12, fontSize:15, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                  Continue <ArrowRight size={16}/>
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Plan & License */}
          {step===3 && (
            <div>
              <h2 style={{ fontSize:18, fontWeight:700, color:'#0f172a', marginBottom:4 }}>Choose Your Plan</h2>
              <p style={{ fontSize:13, color:'#94a3b8', marginBottom:20 }}>Select the plan that fits your team size</p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                {PLANS.map(p=>(
                  <div key={p.id} onClick={()=>setSelectedPlan(p.id)}
                    style={{ background:selectedPlan===p.id?'#fafbff':'#fafafa', border:'2px solid '+(selectedPlan===p.id?p.color:'#e2e8f0'), borderRadius:14, padding:'18px 14px', cursor:'pointer', position:'relative', transition:'all .2s' }}>
                    {(p as any).pop && <span style={{ position:'absolute', top:-10, right:10, background:p.color, color:'#fff', fontSize:9, fontWeight:700, padding:'3px 10px', borderRadius:10, letterSpacing:.3 }}>POPULAR</span>}
                    {selectedPlan===p.id && <div style={{ position:'absolute', top:10, left:10, width:20, height:20, borderRadius:'50%', background:p.color, display:'flex', alignItems:'center', justifyContent:'center' }}><Check size={11} color="#fff"/></div>}
                    <div style={{ fontSize:14, fontWeight:700, color:'#0f172a', marginBottom:2 }}>{p.name}</div>
                    <div style={{ fontSize:22, fontWeight:800, color:p.color }}>{p.price===0?'Free':'\u20B9'+p.price}<span style={{ fontSize:11, color:'#94a3b8', fontWeight:400 }}>{p.price>0?'/mo':''}</span></div>
                    <div style={{ fontSize:11, color:'#64748b', margin:'6px 0 10px' }}>{p.devices===999?'Unlimited':p.devices+' devices'} \u00B7 {p.ret}</div>
                    {p.features.map((f,i)=>(
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#475569', marginBottom:3 }}><Check size={10} color={p.color}/>{f}</div>
                    ))}
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:12, marginTop:24 }}>
                <button onClick={prevStep} style={{ flex:1, padding:14, background:'#f1f5f9', color:'#475569', border:'1px solid #e2e8f0', borderRadius:12, fontSize:14, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                  <ArrowLeft size={16}/> Back
                </button>
                <button onClick={nextStep} style={{ flex:2, padding:14, background:'linear-gradient(135deg,#2563eb,#1d4ed8)', color:'#fff', border:'none', borderRadius:12, fontSize:15, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                  Review & Confirm <ArrowRight size={16}/>
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: Review & Confirm */}
          {step===4 && (
            <div>
              <h2 style={{ fontSize:18, fontWeight:700, color:'#0f172a', marginBottom:4 }}>Review & Confirm</h2>
              <p style={{ fontSize:13, color:'#94a3b8', marginBottom:20 }}>Verify your details before creating your account</p>

              {/* Account Summary */}
              <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:14, padding:20, marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:.5, marginBottom:12 }}>Account Details</div>
                <div style={{ display:'grid', gap:10 }}>
                  {[
                    ['Company', form.companyName],
                    ['Name', form.fullName],
                    ['Email', form.email],
                    ['Phone', form.phone],
                    ['Tools', products.map(p => TOOLS.find(t=>t.key===p)?.name).filter(Boolean).join(', ')],
                  ].map(([l,v],i)=>(
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:13 }}>
                      <span style={{ color:'#64748b' }}>{l}</span>
                      <span style={{ fontWeight:600, color:'#0f172a' }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Plan Summary */}
              <div style={{ background:plan.color+'08', border:'1px solid '+plan.color+'20', borderRadius:14, padding:20, marginBottom:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:.5, marginBottom:4 }}>Selected Plan</div>
                    <div style={{ fontSize:18, fontWeight:800, color:plan.color }}>{plan.name}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:24, fontWeight:800, color:plan.color }}>{plan.price===0?'Free':'\u20B9'+plan.price}</div>
                    {plan.price>0 && <div style={{ fontSize:11, color:'#64748b' }}>per month</div>}
                  </div>
                </div>
                <div style={{ borderTop:'1px solid '+plan.color+'15', paddingTop:10, display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
                  {plan.features.map((f,i)=>(
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#475569' }}><Check size={10} color={plan.color}/>{f}</div>
                  ))}
                </div>
              </div>

              {/* Payment Info */}
              {plan.price > 0 && (
                <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:14, padding:16, marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
                  <CreditCard size={20} color="#2563eb"/>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:'#1e40af' }}>7-day free trial included</div>
                    <div style={{ fontSize:11, color:'#3b82f6' }}>Pay just {'\u20B9'}2 to verify UPI \u00B7 Cancel anytime</div>
                  </div>
                </div>
              )}

              {/* Security badge */}
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:20 }}>
                <Shield size={14} color="#22c55e"/>
                <span style={{ fontSize:11, color:'#64748b' }}>Your data is encrypted with AES-256. We never share your information.</span>
              </div>

              <div style={{ display:'flex', gap:12 }}>
                <button onClick={prevStep} style={{ flex:1, padding:14, background:'#f1f5f9', color:'#475569', border:'1px solid #e2e8f0', borderRadius:12, fontSize:14, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                  <ArrowLeft size={16}/> Back
                </button>
                <button onClick={handleSubmit} disabled={loading}
                  style={{ flex:2, padding:14, background:loading?'#94a3b8':'linear-gradient(135deg,#2563eb,#1d4ed8)', color:'#fff', border:'none', borderRadius:12, fontSize:15, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, opacity:loading?0.7:1, transition:'all .2s' }}>
                  {loading ? <><Loader2 size={16} style={{animation:'spin 1s linear infinite'}}/> Creating Account...</> : <><Sparkles size={16}/> {selectedPlan==='free' ? 'Start Free Plan' : 'Create Account & Pay'}</>}
                </button>
              </div>
            </div>
          )}
        </div>

        <p style={{ textAlign:'center', marginTop:20, fontSize:13, color:'#64748b' }}>
          Already have an account? <a href="/login" style={{ color:'#2563eb', fontWeight:600, textDecoration:'none' }}>Sign in</a>
        </p>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes otpShake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-6px)}40%,80%{transform:translateX(6px)}}`}</style>
    </div>
  );
}